import { useEffect, useState } from 'react';
import { MAX_TIME_LIMIT_MINUTES, MIN_TIME_LIMIT_MINUTES } from '@aiscoreboard/contracts';
import { Toggle } from './Toggle';
import { useT } from '../lib/i18n';

const DEFAULT_LIMIT_MINUTES = 60;

const clampMinutes = (n: number): number =>
  Math.min(MAX_TIME_LIMIT_MINUTES, Math.max(MIN_TIME_LIMIT_MINUTES, n));

/**
 * Optional cap on how long a session may run.
 *
 * Off by default, because most games are decided by frames rather than by the
 * clock, and a timer nobody asked for is just pressure. The minutes row stays
 * on screen while it is off, greyed out rather than hidden: it keeps this
 * panel the same height as whatever sits beside it, and it shows what turning
 * the switch on would give you.
 *
 * It lives here rather than inside the match setup screen because practice
 * needs the same control, and a practice session that ran on a *different*
 * clock — a different default, a different step, a different word for the same
 * idea — would be two features that merely looked alike.
 */
export function TimeLimitField({
  value,
  onChange,
  /** Overrides the line under the control, which differs by what is timed. */
  hint,
  showHint = true,
  /**
   * Tighter spacing, for the setup screen where this shares a panel with the
   * handicap. Practice gives it a panel of its own and keeps the roomier one.
   */
  dense = false,
}: {
  value: number | null;
  onChange: (minutes: number | null) => void;
  hint?: string;
  showHint?: boolean;
  dense?: boolean;
}) {
  const t = useT();
  const enabled = value !== null;
  const [draft, setDraft] = useState(String(value ?? DEFAULT_LIMIT_MINUTES));
  const minutes = value ?? DEFAULT_LIMIT_MINUTES;

  useEffect(() => {
    if (value !== null) setDraft(String(value));
  }, [value]);

  const commit = (raw: string): void => {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(minutes));
      return;
    }
    onChange(clampMinutes(parsed));
  };

  const step = (by: number): void => onChange(clampMinutes(minutes + by));

  const box = dense ? 'h-9 w-9 text-lg' : 'h-11 w-11 text-xl';

  return (
    <div className={`flex w-full flex-col items-center ${dense ? 'gap-1.5' : 'gap-2'}`}>
      {/* Caption beside the switch rather than above it when space is tight:
          the word and the switch are one control, and stacking them spends a
          whole line on a label six characters long. */}
      <div
        className={
          dense
            ? 'flex w-full items-center justify-center gap-2'
            : 'flex flex-col items-center gap-1'
        }
      >
        <span className={`label mb-0 text-center ${enabled ? '' : 'text-white/30'}`}>
          {t('clock.toggle')}
        </span>
        <Toggle
          checked={enabled}
          label={t('clock.toggle')}
          onChange={(on) =>
            onChange(on ? clampMinutes(Number.parseInt(draft, 10) || DEFAULT_LIMIT_MINUTES) : null)
          }
        />
      </div>

      <div
        className={`flex w-full items-center justify-center gap-2 ${enabled ? '' : 'opacity-30'}`}
      >
        <button
          type="button"
          onClick={() => step(-5)}
          disabled={!enabled || minutes <= MIN_TIME_LIMIT_MINUTES}
          aria-label={t('clock.less')}
          className={`step-btn ${box}`}
        >
          −
        </button>

        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label={t('clock.minutes')}
          disabled={!enabled}
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          className={`field shrink-0 px-2 text-center num-lg ${
            dense ? 'w-14 py-1.5 text-xl' : 'w-16 text-2xl'
          }`}
        />

        <button
          type="button"
          onClick={() => step(5)}
          disabled={!enabled || minutes >= MAX_TIME_LIMIT_MINUTES}
          aria-label={t('clock.more')}
          className={`step-btn ${box}`}
        >
          +
        </button>
      </div>

      {/* One line, always, in a line box that is reserved either way. The
          sentence changes when the switch is thrown, and if it were allowed
          to wrap the caption and the stepper above it would jump every time
          the clock was turned on or off. Kept short in all three languages
          instead of balanced across two lines, which is what that costs. */}
      {showHint && <p
        className={`w-full truncate text-center ${
          dense ? 'h-4 text-[0.7rem] leading-4' : 'h-5 text-xs leading-5'
        } ${enabled ? 'text-white/50' : 'text-white/25'}`}
      >
        {enabled ? (hint ?? t('clock.toggleHint')) : t('clock.off')}
      </p>}
    </div>
  );
}
